/* eslint-disable camelcase */
import {
  InventoryMeter,
  SerializedHardwareItem,
  SerializedInventoryMeter,
  UpdateInventoryMeterOutputDto,
} from '@enmon/commons/lib/tools';
import { stringify } from 'qs';
import { RcFile } from 'antd/lib/upload';
import { SelectedReplaceMeterPayload, SelectedReplaceMeterPhotos } from 'routes/meter_replacement/useStore';
import { SelectedServiceInspectionPayload, SelectedServiceInspectionPhotos } from 'routes/service_inspection/types';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import { MeterFileValue } from '@enmon/commons/lib/enmon/files/values';
import { SerializedMeterRegister } from '@enmon/commons';
import { ManualRecord } from '@enmon/commons/lib/enmon';
import { compressFiles, compressFilesArrays } from 'utils/compressFile';
import { SelectedInventorizeMeterPayload, SelectedInventoryPhotos } from '../../routes/inventorize_meter/useStore';
import { BaseApiClient } from './baseApiClient';
import { Authenticator, RequestConfig } from '../httpClient';
import { StrapiApiResponse } from '../../types/strapiResponse';

export type GetInventoryMeterArg = {
  id: SerializedInventoryMeter['id'];
  signal?: AbortSignal;
};
export type GetInventoryMeterResult = SerializedInventoryMeter;

export type GetInventoryMetersArg = { signal?: AbortSignal };
export type GetInventoryMetersResult = StrapiApiResponse<SerializedInventoryMeter>;

export interface ReplaceMeterPayload {
  formPhotos: SelectedReplaceMeterPhotos;
  data: SelectedReplaceMeterPayload;
  inventoryMeterId: InventoryMeter['id'];
}

export interface ServiceInspectionPayload {
  formPhotos: SelectedServiceInspectionPhotos;
  data: SelectedServiceInspectionPayload;
  inventoryMeterId: InventoryMeter['id'];
}

export interface MeterRecord {
  formPhotos: SelectedInventoryPhotos;
  data: SelectedInventorizeMeterPayload;
}

export type FormMeterFileValue = Exclude<MeterFileValue, MeterFileValue.CablePath | MeterFileValue.OptoConnection>;

export interface MeterRegisterInstallationData
  extends Partial<Pick<SerializedMeterRegister, '_id' | 'factor' | 'channel'>> {
  counter: ManualRecord['counter'] | null;
  counterPhotos: File[];
}

export type InstalledMeterHardware = Pick<SerializedHardwareItem, 'id' | 'serial_number'>;

export interface MeterInstallationData {
  data_source_id?: SerializedMeterRegister['dataSourceId'];
  meter_mount_technology?: SerializedInventoryMeter['meter_mount_technology'];

  floorplan_lat?: InventoryMeter['floorplan_lat'];
  floorplan_lng?: InventoryMeter['floorplan_lng'];

  installed_at: Date;
  installed_by: string | null;

  outputs: UpdateInventoryMeterOutputDto[];

  registers: MeterRegisterInstallationData[];
  registers_counters_reading_at?: Date;

  hardwares: InstalledMeterHardware[];
}

export type InstallMeterPhotosMap = {
  [PhotoType in FormMeterFileValue]?: {
    fileList: RcFile[];
    photoContext: PhotoType;
  };
};

export interface InstallationMeterRecord {
  formPhotos: InstallMeterPhotosMap;
  data: MeterInstallationData;
  inventoryMeterId: InventoryMeter['id'];
}

export type MetersDistinctArg = {
  field: 'tenant' | 'owner';
  search?: string;
};

export type MetersDistinctResponse = string[];

export class InventoryMetersApiClient extends BaseApiClient<SerializedInventoryMeter> {
  constructor(authenticator: Authenticator | undefined) {
    super('inventory-meters', authenticator);
  }

  async find({ signal }: GetInventoryMetersArg): Promise<GetInventoryMetersResult> {
    return await this.http.get<GetInventoryMetersResult>('', {
      params: buildQuery<SerializedInventoryMeter>({
        filters: {},
        populate: [
          'inventory_location_building',
          'inventory_meter_api_key',
          'inventory_location',
          'inventory_meter_reading_group',
          'inventory_meter_outputs',
        ],
      }),
      paramsSerializer: params => stringify(params, { allowDots: false }),
      signal,
    });
  }

  async findByApiKey(inventory_meter_api_key: string): Promise<SerializedInventoryMeter[]> {
    return await this.http.get<SerializedInventoryMeter[]>('', {
      params: {
        filters: { inventory_meter_api_key: { api_key: { $eq: inventory_meter_api_key } } },
        populate: { inventory_meter_photos: true },
      },
      paramsSerializer: params => stringify(params, { allowDots: false }),
    });
  }

  async findOne({ id, signal }: GetInventoryMeterArg): Promise<GetInventoryMeterResult> {
    return await this.getOne(`${encodeURIComponent(id)}`, signal, {
      params: buildQuery<SerializedInventoryMeter>({
        filters: {},
        populate: [
          'inventory_location_building',
          'inventory_meter_api_key',
          'inventory_location',
          'inventory_meter_reading_group',
          'inventory_meter_outputs',
        ],
      }),
    });
  }

  async replace(
    payload: ReplaceMeterPayload,
    { authenticator }: Pick<RequestConfig, 'authenticator'> = {},
  ): Promise<SerializedInventoryMeter> {
    const { data, formPhotos, inventoryMeterId } = payload;

    const JSONForm = JSON.stringify(data);
    const formData = new FormData();

    const { detailPhotos, protocolPhotos, oldRegistersPhotos, newRegistersPhotos } = formPhotos;

    const compressedDetailPhotos = await compressFiles(detailPhotos);
    compressedDetailPhotos.forEach((photo, index) =>
      photo ? formData.append(`files.${MeterFileValue.Detail}List.${index}`, photo) : null,
    );
    const compressedProtocolPhotos = await compressFiles(protocolPhotos);
    compressedProtocolPhotos.forEach((photo, index) =>
      photo ? formData.append(`files.${MeterFileValue.Protocol}List.${index}`, photo) : null,
    );

    const compressedOldRegistersPhotos = await compressFilesArrays(oldRegistersPhotos);
    compressedOldRegistersPhotos.forEach((photos, registerIndex) =>
      photos.forEach((photo, photoIndex) =>
        photo ? formData.append(`files.oldRegisterList.${registerIndex}.${photoIndex}`, photo) : null,
      ),
    );

    const compressedNewRegistersPhotos = await compressFilesArrays(newRegistersPhotos);
    compressedNewRegistersPhotos.forEach((photos, registerIndex) =>
      photos.forEach((photo, photoIndex) =>
        photo ? formData.append(`files.newRegisterList.${registerIndex}.${photoIndex}`, photo) : null,
      ),
    );

    formData.append('data', JSONForm);

    return await this.http.put<SerializedInventoryMeter>(`${inventoryMeterId}/replace`, formData, { authenticator });
  }

  async serviceInspection(
    payload: ServiceInspectionPayload,
    { authenticator }: Pick<RequestConfig, 'authenticator'> = {},
  ): Promise<SerializedInventoryMeter> {
    const { data, formPhotos, inventoryMeterId } = payload;
    const { placementOverviewPhotos, registerPhotos } = formPhotos;

    const JSONForm = JSON.stringify(data);
    const formData = new FormData();

    const compressedplacementOverviewPhotos = await compressFiles(placementOverviewPhotos);

    compressedplacementOverviewPhotos.forEach((photo, index) =>
      photo ? formData.append(`files.${MeterFileValue.PlacementOverview}.${index}`, photo) : null,
    );

    await Promise.all(
      registerPhotos.map(async ({ photos, _id: registerId }) => {
        const compressedRegisterPhotos = await compressFiles(photos);

        compressedRegisterPhotos.forEach(photo =>
          photo ? formData.append(`files.counterPhotos.${registerId}`, photo) : null,
        );
      }),
    );

    formData.append('data', JSONForm);
    return await this.http.post<SerializedInventoryMeter>(`${inventoryMeterId}/service-inspection`, formData, {
      authenticator,
    });
  }

  async inventorize(
    payload: MeterRecord,
    { authenticator }: Pick<RequestConfig, 'authenticator'> = {},
  ): Promise<SerializedInventoryMeter> {
    const { data, formPhotos } = payload;

    const JSONForm = JSON.stringify(data);
    const formData = new FormData();

    const {
      originPhotoContextList,
      originPhotoDetailList,
      originPhotoRegisterReadingList,
      originPhotoMeterModuleList,
    } = formPhotos;

    const compressedOriginPhotoContextList = await compressFiles(originPhotoContextList);
    compressedOriginPhotoContextList.forEach((photo, index) =>
      photo ? formData.append(`files.${MeterFileValue.Context}List.${index}`, photo) : null,
    );

    const compressedOriginPhotoDetailList = await compressFiles(originPhotoDetailList);
    compressedOriginPhotoDetailList.forEach((photo, index) =>
      photo ? formData.append(`files.${MeterFileValue.Detail}List.${index}`, photo) : null,
    );

    const compressedOriginPhotoRegisterReadingList = await Promise.all(
      originPhotoRegisterReadingList
        .filter((files): files is (RcFile | undefined)[] => files !== undefined)
        .map(files => compressFiles(files)),
    );
    compressedOriginPhotoRegisterReadingList.forEach((photos, registerIndex) =>
      photos
        ? photos.forEach((photo, photoIndex) =>
            photo ? formData.append(`files.register.${registerIndex}.List.${photoIndex}`, photo) : null,
          )
        : null,
    );

    const compressedOriginPhotoMeterModuleList = await compressFiles(originPhotoMeterModuleList);
    compressedOriginPhotoMeterModuleList.forEach((photo, index) =>
      photo ? formData.append(`files.${MeterFileValue.Module}List.${index}`, photo) : null,
    );

    formData.append('data', JSONForm);

    return await this.http.put<SerializedInventoryMeter>('/inventorize', formData, { authenticator });
  }

  async install(
    payload: InstallationMeterRecord,
    { authenticator }: Pick<RequestConfig, 'authenticator'> = {},
  ): Promise<SerializedInventoryMeter> {
    const { data, inventoryMeterId, formPhotos } = payload;

    const formData = new FormData();
    const registersWithoutCounterPhotos = await Promise.all(
      data.registers.map(async ({ counterPhotos, ...register }) => {
        const compressedCounterPhotos = await compressFiles(counterPhotos);

        compressedCounterPhotos.forEach(photo =>
          photo ? formData.append(`files.counterPhotos.${register._id}`, photo) : null,
        );

        return register;
      }),
    );

    for (const { fileList, photoContext } of Object.values(formPhotos)) {
      if (fileList.length) {
        const compressedFiles = await compressFiles(fileList);

        compressedFiles.forEach((photo, index) =>
          photo ? formData.append(`files.${photoContext}.${index}`, photo) : null,
        );
      }
    }

    formData.append('data', JSON.stringify({ ...data, registers: registersWithoutCounterPhotos }));

    return await this.http.post<SerializedInventoryMeter>(`/${inventoryMeterId}/install`, formData, {
      authenticator,
    });
  }

  async distinct(
    filter: MetersDistinctArg,
    { authenticator }: Pick<RequestConfig, 'authenticator'> = {},
  ): Promise<MetersDistinctResponse> {
    return await this.http.get<MetersDistinctResponse>('/distinct', { params: filter, authenticator });
  }
}
